import React, { useContext, useEffect, useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import {
  Box,
  Heading,
  Badge,
  HStack,
  VStack,
  Text,
  Spinner,
  Button,
  Divider,
  FormControl,
  FormLabel,
  Input,
  useToast,
} from "@chakra-ui/react";
import AuthContext from "../../../contexts/Auth";
import client from "../../../helpers/Client";
import urlToId from "../../../helpers/UrlToId";

export default function AdminUserDetail() {
  const { userId } = useParams();
  const toast = useToast();
  const auth = useContext(AuthContext);
  const isSuperUser = auth.data?.user?.is_superuser;
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "" });
  const [newPassword, setNewPassword] = useState("");
  const [newPassword2, setNewPassword2] = useState("");

  // Hooks must be called unconditionally; avoid early return before effects

  const fetchUser = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/users/${userId}/`);
      setUser(data);
      setForm({ first_name: data.first_name || "", last_name: data.last_name || "", email: data.email || "" });
    } catch (error) {
      toast({ title: "Kullanıcı alınamadı", status: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperUser) return;
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperUser, userId]);

  const saveProfile = async () => {
    try {
      await client.put(`/users/${userId}/`, form);
      toast({ title: "Bilgiler güncellendi", status: "success" });
      fetchUser();
    } catch (error) {
      toast({ title: "Güncelleme başarısız", description: error.response?.data || String(error), status: "error" });
    }
  };

  const errorMessage = (error) => {
    const data = error?.response?.data;
    if (data && typeof data === "object") {
      try {
        return Object.entries(data)
          .map(([k, v]) => Array.isArray(v) ? `${k}: ${v.join(", ")}` : `${k}: ${String(v)}`)
          .join(" | ");
      } catch (_) {
        return JSON.stringify(data);
      }
    }
    return error?.message || String(error);
  };

  const changePassword = async () => {
    if (!newPassword || !newPassword2) {
      toast({ title: "Hata", description: "Şifre alanları boş olamaz", status: "error" });
      return;
    }
    if (newPassword !== newPassword2) {
      toast({ title: "Hata", description: "Şifreler eşleşmiyor", status: "error" });
      return;
    }
    try {
      await client.put(`/users/${userId}/admin_set_password/`, { new_password: newPassword, new_password_2: newPassword2 });
      toast({ title: "Şifre güncellendi", status: "success" });
      setNewPassword("");
      setNewPassword2("");
    } catch (error) {
      toast({ title: "Şifre güncellenemedi", description: errorMessage(error), status: "error" });
    }
  };

  const toggleRole = async (key) => {
    if (!user) return;
    const current =
      key === "is_servicer" ? user.is_servicer :
        key === "is_technical" ? user.is_technical :
          user.employee?.is_manager;
    try {
      await client.put(`/users/${userId}/set_roles/`, { [key]: !current });
      toast({ title: "Rol güncellendi", status: "success" });
      fetchUser();
    } catch (error) {
      toast({ title: "Rol güncellenemedi", status: "error" });
    }
  };

  if (!isSuperUser) {
    return <Navigate to="/ana-sayfa/" replace />;
  }

  return (
    <Box p={6}>
      {loading || !user ? (
        <Spinner />
      ) : (
        <VStack align="stretch" spacing={6}>
          <Box>
            <Heading size="md">{user.first_name} {user.last_name} ({user.username})</Heading>
            <HStack mt={2} spacing={3}>
              <Badge colorScheme={user.is_superuser ? "green" : "gray"}>{user.is_superuser ? "Superuser" : ""}</Badge>
              <Badge colorScheme={user.is_servicer ? "blue" : "gray"}>{user.is_servicer ? "Servisçi" : ""}</Badge>
              <Badge colorScheme={user.is_technical ? "cyan" : "gray"}>{user.is_technical ? "Teknik" : ""}</Badge>
              <Badge colorScheme={user.employee?.is_manager ? "purple" : "gray"}>{user.employee?.is_manager ? "Yönetici" : ""}</Badge>
            </HStack>
          </Box>

          <Box>
            <Heading size="sm" mb={3}>Profil Bilgileri</Heading>
            <VStack align="stretch" spacing={3}>
              <FormControl>
                <FormLabel>Ad</FormLabel>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>Soyad</FormLabel>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>E-posta</FormLabel>
                <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </FormControl>
              <HStack>
                <Button colorScheme="blue" onClick={saveProfile}>Kaydet</Button>
                <Button variant="outline" onClick={fetchUser}>Yenile</Button>
              </HStack>
            </VStack>
          </Box>

          <Divider />

          <Box>
            <Heading size="sm" mb={3}>Roller</Heading>
            <HStack>
              <Button size="sm" onClick={() => toggleRole("is_servicer")}>{user.is_servicer ? "Servisçiyi Kaldır" : "Servisçi Yap"}</Button>
              <Button size="sm" onClick={() => toggleRole("is_technical")}>{user.is_technical ? "Teknik Rolünü Kaldır" : "Teknik Rol Ver"}</Button>
              <Button size="sm" onClick={() => toggleRole("is_manager")}>{user.employee?.is_manager ? "Yöneticiyi Kaldır" : "Yönetici Yap"}</Button>
            </HStack>
          </Box>

          <Divider />

          <Box>
            <Heading size="sm" mb={3}>Şifre</Heading>
            <HStack>
              <Input type="password" placeholder="Yeni şifre" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
              <Input type="password" placeholder="Yeni şifre (tekrar)" value={newPassword2} onChange={(e) => setNewPassword2(e.target.value)} />
              <Button colorScheme="purple" onClick={changePassword}>Güncelle</Button>
            </HStack>
          </Box>

          {user.employee && (
            <Box>
              <Heading size="sm" mb={3}>Personel Bilgileri</Heading>
              <VStack align="stretch" spacing={1}>
                <Text>Reseller: {user.employee?.reseller?.name || "-"}</Text>
                <Text>Yönetici: {user.employee?.is_manager ? "Evet" : "Hayır"}</Text>
                <Text>Takım üyelikleri: {(user.employee?.team_memberships || []).map(m => m.team?.name).join(", ") || "-"}</Text>
              </VStack>
            </Box>
          )}

          {user.customer && (
            <Box>
              <Heading size="sm" mb={3}>Müşteri Bilgileri</Heading>
              <VStack align="stretch" spacing={1}>
                <Text>Telefon: {user.customer?.phone_number || "-"}</Text>
                <Text>Adres: {user.customer?.address || "-"}</Text>
              </VStack>
            </Box>
          )}
        </VStack>
      )}
    </Box>
  );
}